'use client'

import { useCallback } from 'react'
import { Box, Grid, GridItem, Text, VStack } from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'
import { APIProvider } from '@vis.gl/react-google-maps'
import { GrandHeading, GrandSection } from 'ui'
import type { MapLocatorLayout, MapLocatorWidgetType, StorefrontWidget } from 'ecosystem'
import { type WidgetImplementation } from '../../registry/typings'
import { DEFAULT_HEIGHT, MAP_LOCATOR_CN, mapLocatorCn as cn, MAPS_LIBRARIES } from './constants'
import { useMapLocator, useMapLocatorSettings, type MapLocatorEntry } from './useMapLocator'
import MapCanvas from './MapCanvas'
import FilterBar from './FilterBar'
import ResultsList from './ResultsList'
import PlaceSearch from './PlaceSearch'
import LocationPanel from './LocationPanel'

export type MapLocatorWidgetProps = WidgetImplementation<StorefrontWidget<MapLocatorWidgetType>>

const LAYOUT_COLUMNS: Record<MapLocatorLayout, { base: string; md: string }> = {
  MAP_ONLY: { base: '1fr', md: '1fr' },
  MAP_WITH_LIST: { base: '1fr', md: '2fr 1fr' },
  LIST_WITH_MAP: { base: '1fr', md: '1fr 2fr' }
}

const MapLocatorBody = ({ widget }: { widget: MapLocatorWidgetType }) => {
  const settings = useMapLocatorSettings(widget)
  const {
    entries,
    selectedTagIds,
    toggleTag,
    query,
    setQuery,
    origin,
    setOrigin,
    activeItem,
    activeItemId,
    selectItem,
    focus,
    clearFilters,
    hasActiveFilters,
    isFiltering
  } = useMapLocator({ items: widget.items ?? [], settings })

  /** A marker click only recentres — the visitor is already looking at the point. */
  const handleSelectOnMap = useCallback(
    (item: MapLocatorEntry | null) => selectItem(item),
    [selectItem]
  )

  /** A list click has to zoom in too, or the point stays hidden inside its cluster. */
  const handleSelectFromList = useCallback(
    (item: MapLocatorEntry) => selectItem(item, { zoomIn: true }),
    [selectItem]
  )

  const handleClosePanel = useCallback(() => selectItem(null), [selectItem])

  const showsList = settings.layout !== 'MAP_ONLY' && settings.search.showList
  const showsPanel = settings.popover.mode === 'SIDE_PANEL' && Boolean(activeItem)
  const height = { ...DEFAULT_HEIGHT, ...widget.height }
  const mapArea = (
    // `Map` ignores its `style` prop once a className is set, so the size rule
    // is applied to the map container from here.
    <Box
      h="full"
      overflow="hidden"
      rounded="md"
      sx={{ [`.${cn(MAP_LOCATOR_CN.MAP)}`]: { w: 'full', h: 'full' } }}
      w="full"
    >
      <MapCanvas
        activeItem={activeItem}
        entries={entries}
        focus={focus}
        onSelect={handleSelectOnMap}
        origin={origin}
        settings={settings}
      />
    </Box>
  )

  return (
    <VStack align="stretch" className={cn(MAP_LOCATOR_CN.WRAPPER)} spacing={4} w="full">
      <VStack align="stretch" className={cn(MAP_LOCATOR_CN.TOOLBAR)} spacing={3}>
        <PlaceSearch
          countryBias={settings.map.countryBias}
          labels={settings.labels}
          onOriginChange={setOrigin}
          onQueryChange={setQuery}
          origin={origin}
          query={query}
          search={settings.search}
        />

        <FilterBar
          hasActiveFilters={hasActiveFilters}
          labels={settings.labels}
          onClear={clearFilters}
          onToggle={toggleTag}
          resultCount={entries.length}
          selectedTagIds={selectedTagIds}
          tags={settings.tags}
        />
      </VStack>

      <Grid
        className={cn(MAP_LOCATOR_CN.BODY)}
        gap={4}
        templateColumns={LAYOUT_COLUMNS[settings.layout]}
      >
        <GridItem h={height} order={{ base: 1, md: settings.layout === 'LIST_WITH_MAP' ? 2 : 1 }}>
          {mapArea}
        </GridItem>

        {showsList ? (
          <GridItem
            borderWidth="1px"
            h={height}
            // Dimmed while the deferred filter catches up, so results that are a
            // beat behind the input read as pending rather than as wrong
            opacity={isFiltering ? 0.6 : 1}
            order={{ base: 2, md: settings.layout === 'LIST_WITH_MAP' ? 1 : 2 }}
            overflow="hidden"
            rounded="md"
            transition="opacity 120ms ease"
          >
            <ResultsList
              activeItemId={activeItemId}
              entries={entries}
              labels={settings.labels}
              onSelect={handleSelectFromList}
              showDistance={settings.search.showDistance}
              tags={settings.tags}
            />
          </GridItem>
        ) : null}
      </Grid>

      {showsPanel && activeItem ? (
        <LocationPanel
          item={activeItem}
          labels={settings.labels}
          onClose={handleClosePanel}
          popover={settings.popover}
          showDistance={settings.search.showDistance}
          tags={settings.tags}
        />
      ) : null}
    </VStack>
  )
}

export function MapLocatorWidget({ widget, widgetId }: MapLocatorWidgetProps) {
  const { title, description } = widget.widget
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''

  // The Maps script is heavy; it is only requested once the section approaches the viewport.
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '200px' })

  return (
    <GrandSection
      className={cn(MAP_LOCATOR_CN.SECTION)}
      contentContainerProps={{ className: cn(MAP_LOCATOR_CN.SECTION_CONTAINER) }}
      ref={ref}
      sectionId={widgetId}
    >
      <VStack align="stretch" spacing={3} w="full">
        {title ? <GrandHeading className={cn(MAP_LOCATOR_CN.TITLE)} title={title} /> : null}

        {description ? (
          <Text className={cn(MAP_LOCATOR_CN.DESC)} color="gray.600">
            {description}
          </Text>
        ) : null}

        {inView && apiKey ? (
          <APIProvider apiKey={apiKey} libraries={MAPS_LIBRARIES}>
            <MapLocatorBody widget={widget.widget} />
          </APIProvider>
        ) : (
          <Box h={{ ...DEFAULT_HEIGHT, ...widget.widget.height }} />
        )}
      </VStack>
    </GrandSection>
  )
}

export default MapLocatorWidget
